'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MessageSquarePlus, X, Loader2, CheckCircle2, AlertTriangle, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import type { FeedbackCategory, FeedbackSeverity } from '@/lib/types/feedback';
import { CATEGORY_LABELS } from '@/lib/types/feedback';

const CATEGORIES: { value: FeedbackCategory; label: string }[] = [
  { value: 'bug', label: '🐛 Bug' },
  { value: 'feature', label: '💡 Feature' },
  { value: 'complaint', label: '😤 Issue' },
  { value: 'praise', label: '🙌 Praise' },
  { value: 'question', label: '❓ Question' },
];

export function FeedbackWidget() {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [category, setCategory] = useState<FeedbackCategory>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<FeedbackSeverity>('medium');

  const reset = () => {
    setCategory('bug');
    setTitle('');
    setDescription('');
    setSeverity('medium');
    setSubmitted(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category,
          severity,
          title: title.trim(),
          description: description.trim(),
          page_url: pathname,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to submit');
      }

      setSubmitted(true);
      toast.success('Feedback submitted — thanks!');
      setTimeout(() => {
        setIsOpen(false);
        reset();
      }, 2000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't show on auth pages or landing page
  if (pathname?.startsWith('/auth') || pathname === '/') return null;

  return (
    <>
      {/* Floating trigger button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-4 z-50 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all active:scale-95"
          aria-label="Send feedback"
        >
          <MessageSquarePlus className="h-5 w-5" />
        </button>
      )}

      {/* Feedback modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => { setIsOpen(false); reset(); }}
          />

          {/* Panel */}
          <Card className="relative z-10 w-full max-w-md mx-4 mb-4 sm:mb-0 shadow-2xl border-0">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquarePlus className="h-5 w-5 text-blue-600" />
                  Send Feedback
                </CardTitle>
                <button
                  onClick={() => { setIsOpen(false); reset(); }}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </CardHeader>

            <CardContent>
              {submitted ? (
                <div className="flex flex-col items-center py-6 gap-3">
                  <CheckCircle2 className="h-12 w-12 text-green-500" />
                  <p className="text-lg font-medium">Thanks for your feedback!</p>
                  <p className="text-sm text-gray-500">We&apos;ll review it shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Category pills */}
                  <div>
                    <Label className="text-sm font-medium mb-2 block">What&apos;s this about?</Label>
                    <div className="flex flex-wrap gap-2">
                      {CATEGORIES.map((cat) => (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => setCategory(cat.value)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            category === cat.value
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Severity (only for bugs/complaints) */}
                  {(category === 'bug' || category === 'complaint') && (
                    <div>
                      <Label className="text-sm font-medium mb-2 block">How serious?</Label>
                      <div className="flex gap-2">
                        {(['low', 'medium', 'high', 'critical'] as FeedbackSeverity[]).map((sev) => (
                          <button
                            key={sev}
                            type="button"
                            onClick={() => setSeverity(sev)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              severity === sev
                                ? sev === 'critical' ? 'bg-red-600 text-white'
                                : sev === 'high' ? 'bg-orange-500 text-white'
                                : sev === 'medium' ? 'bg-yellow-500 text-white'
                                : 'bg-green-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {sev.charAt(0).toUpperCase() + sev.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Title */}
                  <div>
                    <Label htmlFor="fb-title" className="text-sm font-medium">
                      Title
                    </Label>
                    <Input
                      id="fb-title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Brief summary"
                      maxLength={200}
                      required
                      className="mt-1"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <Label htmlFor="fb-desc" className="text-sm font-medium">
                      Details
                    </Label>
                    <textarea
                      id="fb-desc"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="What happened? What did you expect?"
                      maxLength={5000}
                      required
                      rows={3}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting || !title.trim() || !description.trim()}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Feedback
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
